import React, { FC, useEffect, useState } from "react";
import { CartItem } from "../../../typings/interfaces/pos/cart-item";
import CustomModal from "../../common/custom-modal/custom-modal";
import { Box, Typography } from "@mui/material";
import { Colors } from "../../../configs";
import { CustomButton } from "../../atoms";
import { updateCartSessionItem } from "../../../redux/actions/cartAction";
import { cloneDeep } from "lodash";
import { useAppDispatch } from "../../../hooks";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

export interface IItemNoteModalProps {
  open: boolean;
  onClose(): void;
  cartItem: CartItem;
}

const ItemNoteModal: FC<IItemNoteModalProps> = ({
  open,
  onClose,
  cartItem,
}) => {
  const [note, setNote] = useState(cartItem.note || "");
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const [isFocused, setIsFocused] = useState(false);

  const addOrUpdateCartItemNote = async () => {
    if (note.length > 0) {
      if (cartItem.note !== note) {
        const tempCartItem = cloneDeep(cartItem);
        tempCartItem.note = note;
        await dispatch(updateCartSessionItem({ cartItem: tempCartItem }));
        onClose();
      } else {
        snackbar.showSnackbar("note must be edited before update", "warning");
      }
    } else {
      snackbar.showSnackbar("note can't be empty", "warning");
    }
  };
  useEffect(() => {
    setNote(cartItem.note || "");
  }, [cartItem.note, open]);

  const removeCartItemNote = async () => {
    const tempCartItem = cloneDeep(cartItem);
    tempCartItem.note = null;
    await dispatch(updateCartSessionItem({ cartItem: tempCartItem }));
    onClose();
  };

  return (
    <CustomModal
      title={"Cart Item Note"}
      openModal={open}
      width="40rem"
      height="38rem"
      onClose={onClose}
    >
      <Box
        sx={{
          paddingTop: "2rem",
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8rem",
            flex: 1,
          }}
        >
          <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "1.2rem", fontWeight: 600 }}>
            Note about this cart item:
          </Typography>
          <textarea
            name="cart-item-note"
            autoFocus
            placeholder="Add your note here..."
            style={{
              border: `0.0714rem solid ${isFocused ? Colors.SECONDARY : Colors.ACCENT_GRAY_DISABLED
                }`,
              outline: isFocused ? `0.15rem solid ${Colors.SECONDARY}` : "none",
              borderRadius: "0.4rem",
              padding: "1rem",
              fontSize: "1.142rem",
              height: "15rem",
              width: "100%",
              resize: "none",
              transition: "all 0.2s ease",
            }}
            onChange={(e) => setNote(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            value={note}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: "1.5rem",
            marginTop: "auto",
            paddingTop: "2rem",
          }}
        >
          <CustomButton
            sx={{
              height: "3.85714286rem",
              flex: 1,
              fontSize: "1.2rem",
              fontWeight: 600,
              background: Colors.WHITE,
              color: Colors.DISABLED_GREEN,
              border: `0.1114285714rem solid ${Colors.DISABLED_GREEN}`,
              "&:hover": {
                background: Colors.WHITE,
              },
              borderRadius: "0.4rem",
            }}
            name="remove-cart-item-note"
            disabled={!cartItem?.note}
            onClick={removeCartItemNote}
          >
            Remove Note
          </CustomButton>
          <CustomButton
            sx={{
              height: "3.85714286rem",
              flex: 1,
              fontSize: "1.2rem",
              fontWeight: 600,
              background: Colors.PRIMARY,
              borderRadius: "0.4rem",
            }}
            name="add-or-update-cart-item-note"
            disabled={note === cartItem?.note || note === ""}
            onClick={addOrUpdateCartItemNote}
          >
            {cartItem?.note ? "Update Note" : "Add Note"}
          </CustomButton>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default ItemNoteModal;
